import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';
import GalleryComposer, {
  emptyGalleryPost, postFromExisting, validateGalleryPosts, galleryPostsToPayload,
} from '@/components/GalleryComposer';

/**
 * Create/edit a single gallery post (approved-DJ owner only). Reuses
 * GalleryComposer in single-post mode so all validation + media handling
 * (upload, reorder, remove, limits) is shared with the application composer.
 *
 * Props:
 *  - open, onOpenChange
 *  - post: existing post (owner view incl. id) to edit, or null to create
 *  - onSaved: () => void   (caller refetches the gallery)
 */
export default function GalleryPostEditor({ open, onOpenChange, post, onSaved }) {
  const isEdit = !!post?.id;
  const [draft, setDraft] = useState([emptyGalleryPost()]);
  const [saving, setSaving] = useState(false);

  // Reseed whenever the dialog opens (create => blank, edit => from existing).
  useEffect(() => {
    if (open) setDraft([isEdit ? postFromExisting(post) : emptyGalleryPost()]);
  }, [open, isEdit, post]);

  const save = async () => {
    const err = validateGalleryPosts(draft);
    if (err) { toast.error(err); return; }
    const body = galleryPostsToPayload(draft)[0];   // single post
    setSaving(true);
    try {
      if (isEdit) await api.put(`/users/me/dj-gallery/${post.id}`, body);
      else await api.post('/users/me/dj-gallery', body);
      toast.success(isEdit ? 'Post updated' : 'Post added');
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit gallery post' : 'Add gallery post'}</DialogTitle>
        </DialogHeader>
        <GalleryComposer posts={draft} setPosts={setDraft} disabled={saving}
          hideAddPost hideRemovePost headerless />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add post')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
